import React, { FC, useState } from 'react';
import { generateFontImage } from 'src/components/ArtworkApproval/utils/utils';
import { ApplicationMethod, ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { Customisation, Customisation as CustomisationType } from 'src/components/BundleProduct/types';
import { MultipleStepCustomisation } from 'src/components/BundleProduct/MultipleStepCustomisation/MultipleStepCustomisation';
import { ProductMainPrice } from 'src/components/BundleProduct/ProductMainPrice';
import { camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations } from 'src/queries/__generated__/wearerBundleDetail';
import snakecaseKeys from 'snakecase-keys';

import classes from './MixAndMatchChosenCustomisation.scss';

type CustomisationCost = {
    regularPrice: number;
    finalPrice: number;
    fee: number;
};

type Props = {
    locations: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations[];
    recommendedApplication?: ApplicationMethod;
    customisation: CustomisationType;
    onEdit: (state: Customisation) => void;
    onDelete: (index: number) => void;
    index: number;
    customisationCost: CustomisationCost;
    alreadySelectedPositions?: string[];
};

const CustomizationTitle: React.FC<{ customisation: CustomisationType }> = ({ customisation }) => {
    if (customisation.selectedMethod === ApplicationMethod.Embroidery.toString()) {
        if (customisation.selectedType === 'text')
            return (
                <span className={classes.customisationTitle}>
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/embroidery.svg')}
                        alt=""
                    />
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/text.svg')}
                        alt=""
                    />
                    Embroidery Text
                </span>
            );
        else
            return (
                <span className={classes.customisationTitle}>
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/embroidery.svg')}
                        alt=""
                    />
                    Embroidery
                </span>
            );
    } else {
        if (customisation.selectedType === 'text')
            return (
                <span className={classes.customisationTitle}>
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/print.svg')}
                        alt=""
                    />
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/text.svg')}
                        alt=""
                    />
                    Printed Text
                </span>
            );
        else
            return (
                <span className={classes.customisationTitle}>
                    <img
                        className={classes.customisationIcon}
                        src={require('../components/Svg/svgs/print.svg')}
                        alt=""
                    />
                    Print
                </span>
            );
    }
};

export const MixAndMatchChosenCustomisation: FC<Props> = ({
    locations,
    recommendedApplication,
    customisation,
    onEdit,
    onDelete,
    index,
    customisationCost,
    alreadySelectedPositions,
}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const freeCustomisation = index === 0;
    const placeholder = require('../../static/img/placeholder.jpg');
    const placeholderLogo = require('../../static/img/placeholder-logo.jpg');

    return (
        <div className={classes.container}>
            <div className={classes.ImageAndTitleContainer}>
                <figure className={classes.customisationImage}>
                    {(customisation.selectedType === 'logo' && customisation.selectedOptions && (
                        <>
                            {'isContactRequested' in customisation.selectedOptions && (
                                <img src={placeholderLogo} alt="We will contact you" className={classes.img} />
                            )}
                            {'image' in customisation.selectedOptions && (
                                <img
                                    src={customisation.selectedOptions?.image || placeholder}
                                    alt="Customisation logo"
                                    className={classes.img}
                                />
                            )}
                        </>
                    )) || <img src={placeholder} alt="Placeholder logo" className={classes.img} />}
                    {customisation.selectedType === 'text' && generateFontImage(customisation.selectedOptions)}
                </figure>
                <div className={classes.customisationInfoContainer}>
                    <CustomizationTitle customisation={customisation} />
                    <div className={classes.customisationPositionsContainer}>
                        <span className={classes.customisationPositions}>{customisation.selectedPositions}</span>
                        <button
                            type="button"
                            className={classes.editButtonDesktop}
                            onClick={() => setIsModalOpen(true)}
                        >
                            Edit
                        </button>
                    </div>
                </div>
            </div>
            <div className={classes.PriceAndRemoveContainer}>
                <button type="button" className={classes.editButtonMobile} onClick={() => setIsModalOpen(true)}>
                    Edit
                </button>
                <span className={classes.price}>
                    {freeCustomisation ? (
                        'Free'
                    ) : (
                        <ProductMainPrice
                            price={{
                                price: customisationCost.regularPrice,
                                price_incl: customisationCost.regularPrice,
                            }}
                        />
                    )}
                </span>
                <button className={classes.removeButton} type="button" onClick={() => onDelete(index)}>
                    <img
                        className={classes.removeButtonIcon}
                        src={require('../../src/svg/MixAndMatchBin.svg')}
                        alt=""
                    />
                    Remove
                </button>
            </div>

            {isModalOpen && (
                <MultipleStepCustomisation
                    locations={snakecaseKeys(locations as any) as unknown as ProductLocation[]}
                    isOpenModal={isModalOpen}
                    closeModal={() => setIsModalOpen(false)}
                    onSubmitCustomisation={onEdit}
                    customisation={customisation}
                    freeCustomisation={freeCustomisation}
                    avoidSelectingPosition={index !== 0}
                    recommendedApplication={recommendedApplication}
                    alreadySelectedPositions={alreadySelectedPositions}
                />
            )}
        </div>
    );
};
